import React, { useState, useEffect, useCallback } from "react";
import t from "../../service/translate.service";
import { Spin, Alert } from "antd";
import { buttonStyles, sectionStyles } from "./styles";
import useVoucherRequest, { VOUCHER_TYPES } from "../../hooks/useVoucherRequest";
import { api } from "../../service/api.service";

/**
 * Error/Status codes matching backend ELIGIBILITY_ERROR_CODES
 */
const ELIGIBILITY_ERROR_CODES = {
  CONTRACT_NOT_STARTED: 'CONTRACT_NOT_STARTED',
  HANDOVER_PENDING: 'HANDOVER_PENDING',
  HAS_DEBT: 'HAS_DEBT',
  VOUCHER_NOT_ELIGIBLE: 'VOUCHER_NOT_ELIGIBLE',
  CONTRACT_NOT_ACTIVE: 'CONTRACT_NOT_ACTIVE',
  VOUCHER_REQUESTED: 'VOUCHER_REQUESTED',
  // Request status codes from database
  VOUCHER_PENDING: 'VOUCHER_PENDING',
  VOUCHER_APPROVED: 'VOUCHER_APPROVED',
  VOUCHER_COMPLETED: 'VOUCHER_COMPLETED',
  VOUCHER_REJECTED: 'VOUCHER_REJECTED'
};

/**
 * Get message based on status/error code
 * @param {string} errorCode - The error/status code
 * @param {string} voucherType - 'marketing' or 'capex'
 * @param {string} contractNumber - The contract number for placeholder replacement
 */
const getMessage = (errorCode, voucherType, contractNumber = '') => {
  const voucherName = voucherType === 'marketing' 
    ? (t['VOUCHER_MARKETING_TITLE'] || 'Marketing voucher')
    : (t['VOUCHER_CAPEX_TITLE'] || 'CAPEX voucher');

  // Get the contact message based on voucher type
  const getContactMessage = () => {
    if (voucherType === 'marketing') {
      return t['VOUCHER_CONTACT_LETTING_MANAGER'] || 'Please contact your letting manager';
    }
    return t['VOUCHER_CONTACT_SERVICE_CENTER'] || 'Please contact your service center';
  };

  // Get the specific reason for not being eligible
  const getReasonText = (code) => {
    switch (code) {
      case ELIGIBILITY_ERROR_CODES.CONTRACT_NOT_STARTED:
        return t['VOUCHER_REASON_CONTRACT_NOT_STARTED'] || 'Your contract has not started yet';
      case ELIGIBILITY_ERROR_CODES.HANDOVER_PENDING:
        return t['VOUCHER_REASON_HANDOVER_PENDING'] || 'The property handover is pending';
      case ELIGIBILITY_ERROR_CODES.HAS_DEBT:
        return t['VOUCHER_REASON_HAS_DEBT'] || 'There are outstanding payments on your account';
      case ELIGIBILITY_ERROR_CODES.CONTRACT_NOT_ACTIVE:
        return t['VOUCHER_REASON_CONTRACT_NOT_ACTIVE'] || 'Your contract is not active';
      default:
        return null;
    }
  };

  switch (errorCode) {
    case ELIGIBILITY_ERROR_CODES.VOUCHER_REQUESTED:
    case ELIGIBILITY_ERROR_CODES.VOUCHER_PENDING:
      return t['VOUCHER_PENDING_MESSAGE'] 
        || 'Thank you for your voucher request. Processing your request will take approximately 5-7 business days.';
    case ELIGIBILITY_ERROR_CODES.VOUCHER_APPROVED:
      return t['VOUCHER_APPROVED_MESSAGE'] 
        ? t['VOUCHER_APPROVED_MESSAGE'].replace('{voucherName}', voucherName)
        : `Your ${voucherName} request has been approved. You will receive it soon.`;
    case ELIGIBILITY_ERROR_CODES.VOUCHER_COMPLETED:
      return t['VOUCHER_COMPLETED_MESSAGE'] 
        ? t['VOUCHER_COMPLETED_MESSAGE'].replace('{voucherName}', voucherName)
        : `Your ${voucherName} has been granted. Check your vouchers list.`;
    case ELIGIBILITY_ERROR_CODES.CONTRACT_NOT_STARTED:
    case ELIGIBILITY_ERROR_CODES.HANDOVER_PENDING:
    case ELIGIBILITY_ERROR_CODES.HAS_DEBT:
    case ELIGIBILITY_ERROR_CODES.CONTRACT_NOT_ACTIVE:
    case ELIGIBILITY_ERROR_CODES.VOUCHER_NOT_ELIGIBLE:
    default:
      // Build friendly message with reason in brackets
      const baseMessage = t['VOUCHER_ERROR_NOT_ELIGIBLE_BASE'] 
        || 'Dear tenant, after reviewing the available contract data, the requirements for applying for a voucher are currently not met.';
      const reason = getReasonText(errorCode);
      const contact = getContactMessage();
      
      if (reason) {
        return `${baseMessage} (${reason}). ${contact}`;
      }
      return `${baseMessage} ${contact}`;
  }
};

/**
 * Check if the status code is an info/success message (not an error)
 */
const isInfoMessage = (errorCode) => {
  return [
    ELIGIBILITY_ERROR_CODES.VOUCHER_REQUESTED,
    ELIGIBILITY_ERROR_CODES.VOUCHER_PENDING,
    ELIGIBILITY_ERROR_CODES.VOUCHER_APPROVED,
    ELIGIBILITY_ERROR_CODES.VOUCHER_COMPLETED
  ].includes(errorCode);
};


/**
 * VoucherRequestButtons component
 * Displays request sections for Marketing and CAPEX vouchers based on eligibility
 */
const VoucherRequestButtons = ({ eligibility, loading, contractNumber, tenantName, address }) => {

  const {
    requestMarketingVoucher,
    requestCapexVoucher,
    loading: requestLoading,
    error: requestError,
    success: requestSuccess,
    successMessage,
    resetState
  } = useVoucherRequest();

  // Track request status from database
  const [requestStatus, setRequestStatus] = useState({
    marketing: { requested: false },
    capex: { requested: false }
  });
  const [statusLoading, setStatusLoading] = useState(true);

  // Fetch request status from database
  const fetchRequestStatus = useCallback(async () => {
    if (!contractNumber) {
      setStatusLoading(false);
      return;
    }
    
    try {
      const response = await api.get(`/vouchers/${contractNumber}/request-status`);
      setRequestStatus(response.data || { marketing: { requested: false }, capex: { requested: false } });
    } catch (error) {
      console.error('VoucherRequestButtons: error fetching request status', error);
    } finally {
      setStatusLoading(false);
    }
  }, [contractNumber]);

  // Fetch status on mount and when contractNumber changes
  useEffect(() => {
    fetchRequestStatus();
  }, [fetchRequestStatus]);

  if (loading || statusLoading) {
    return (
      <div className="voucher-request-section" style={{ marginTop: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!eligibility) {
    return null;
  }

  // Map DB status to error code
  const getStatusErrorCode = (status) => {
    switch (status) {
      case 'pending':
        return ELIGIBILITY_ERROR_CODES.VOUCHER_PENDING;
      case 'approved':
        return ELIGIBILITY_ERROR_CODES.VOUCHER_APPROVED;
      case 'completed':
        return ELIGIBILITY_ERROR_CODES.VOUCHER_COMPLETED;
      case 'rejected':
        return null; // Rejected requests allow re-requesting
      default:
        return null;
    }
  };

  // Combine API eligibility with DB request status
  const getEffectiveEligibility = (apiEligibility, voucherType) => {
    const dbStatus = requestStatus[voucherType];
    
    // If request exists in DB, check the status
    if (dbStatus?.requested && dbStatus?.status) {
      const errorCode = getStatusErrorCode(dbStatus.status);
      
      // If rejected, allow re-requesting (use API eligibility)
      if (dbStatus.status === 'rejected') {
        return apiEligibility;
      }
      
      // For pending, approved, completed - show status and disable button
      return {
        eligible: true,
        canRequest: false,
        errorCode: errorCode
      };
    }
    
    return apiEligibility;
  };

  const marketing = getEffectiveEligibility(eligibility.marketing, 'marketing');
  const capex = getEffectiveEligibility(eligibility.capex, 'capex');

  const handleRequestMarketing = async () => {
    const result = await requestMarketingVoucher({
      contractNumber,
      tenantName,
      address
    });
    if (result.success) {
      fetchRequestStatus();
    }
  };

  const handleRequestCapex = async () => {
    const result = await requestCapexVoucher({
      contractNumber,
      tenantName,
      address
    });
    if (result.success) {
      fetchRequestStatus();
    }
  };

  return (
    <div className="voucher-request-section" style={sectionStyles.container}>
      {/* Success Banner */}
      {requestSuccess && (
        <Alert
          message={t['VOUCHER_REQUEST_SUCCESS_TITLE'] || 'Request Submitted'}
          description={successMessage || t['VOUCHER_REQUEST_SUCCESS_MESSAGE'] || 'Your voucher request has been submitted successfully.'}
          type="success"
          showIcon
          style={{ marginBottom: '16px' }}
          closable
          onClose={resetState}
        />
      )}

      {/* Error Message */}
      {requestError && (
        <Alert
          message={requestError}
          type="error"
          showIcon
          style={{ marginBottom: '16px' }}
          closable
          onClose={resetState}
        />
      )}

      {/* Marketing Vouchers Section */}
      <div className="voucher-card" style={sectionStyles.card}>
        <h3 style={sectionStyles.title}>
          {t['VOUCHER_MARKETING_SECTION_TITLE'] || 'Marketing vouchers'}
        </h3>
        <p style={sectionStyles.description}>
          {t['VOUCHER_MARKETING_DESCRIPTION'] || 'As a welcome gift, you can request a marketing voucher for your new home. This voucher can be redeemed at selected partner stores.'}
        </p>
        
        {!marketing?.canRequest && marketing?.errorCode && (
          <div style={isInfoMessage(marketing.errorCode) ? sectionStyles.infoMessageSuccess : sectionStyles.infoMessage}>
            <div style={isInfoMessage(marketing.errorCode) ? sectionStyles.infoIconSuccess : sectionStyles.infoIcon}>i</div>
            <span style={isInfoMessage(marketing.errorCode) ? sectionStyles.infoTextSuccess : sectionStyles.infoText}>
              {getMessage(marketing.errorCode, 'marketing', contractNumber)}
            </span>
          </div>
        )}
        
        {/* Hide button when not eligible (VOUCHER_NOT_ELIGIBLE), show for other states */}
        {marketing?.errorCode !== ELIGIBILITY_ERROR_CODES.VOUCHER_NOT_ELIGIBLE && (
          <button
            className="voucher-request-button"
            onClick={handleRequestMarketing}
            disabled={!marketing?.canRequest || requestLoading}
            style={marketing?.canRequest && !requestLoading ? buttonStyles.enabled : buttonStyles.disabled}
          >
            {requestLoading ? <Spin size="small" /> : (t['VOUCHER_REQUEST_MARKETING_BTN'] || 'Request Marketing voucher')}
          </button>
        )}
      </div>

      {/* CAPEX Vouchers Section */}
      <div className="voucher-card" style={{ ...sectionStyles.card, borderBottom: 'none' }}>
        <h3 style={sectionStyles.title}>
          {t['VOUCHER_CAPEX_SECTION_TITLE'] || 'CAPEX vouchers'}
        </h3>
        <p style={sectionStyles.description}>
          {t['VOUCHER_CAPEX_DESCRIPTION'] || 'Request a CAPEX voucher for home improvements and renovations. This voucher helps cover costs for upgrades to your rental property.'}
        </p>
        
        {!capex?.canRequest && capex?.errorCode && (
          <div style={isInfoMessage(capex.errorCode) ? sectionStyles.infoMessageSuccess : sectionStyles.infoMessage}>
            <div style={isInfoMessage(capex.errorCode) ? sectionStyles.infoIconSuccess : sectionStyles.infoIcon}>i</div>
            <span style={isInfoMessage(capex.errorCode) ? sectionStyles.infoTextSuccess : sectionStyles.infoText}>
              {getMessage(capex.errorCode, 'capex', contractNumber)}
            </span>
          </div>
        )}
        
        {/* Hide button when not eligible (VOUCHER_NOT_ELIGIBLE), show for other states */}
        {capex?.errorCode !== ELIGIBILITY_ERROR_CODES.VOUCHER_NOT_ELIGIBLE && (
          <button
            className="voucher-request-button"
            onClick={handleRequestCapex}
            disabled={!capex?.canRequest || requestLoading}
            style={capex?.canRequest && !requestLoading ? buttonStyles.enabled : buttonStyles.disabled}
          >
            {requestLoading ? <Spin size="small" /> : (t['VOUCHER_REQUEST_CAPEX_BTN'] || 'Request CAPEX voucher')}
          </button>
        )}
      </div>

    </div>
  );
};

export default VoucherRequestButtons;
